import { Button } from "@/components/ui/button";
import Spinner from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Image } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import { ComboBoxOptionType, TooltipProps } from ".";
import { useSelector } from "react-redux";
import { useSession } from "@clerk/clerk-react";
import { getUnexpiredToken } from "@/utils/utils";

interface CustomImageUploadProps {
  value: string;
  evaluatedValue: string;
  set: boolean;
  setSet: (set: boolean) => void;
  buildOptionPreview: (option: ComboBoxOptionType) => React.ReactNode;
  onClear: () => void;
  setValue: (val: string) => void;
  onComboboxSelected: (option: ComboBoxOptionType) => void;
  buildArbitraryClass: (input: string) => ComboBoxOptionType;
}

const CustomImageUpload: React.FC<CustomImageUploadProps & TooltipProps> = ({
  value,
  setValue,
  setSet,
  buildArbitraryClass,
  onComboboxSelected,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isImageUploading, setIsImageUploading] = React.useState(false);

  const { session } = useSession();

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0] && !isImageUploading) {
      // handle upload to gcp
      const file = event.target.files[0];

      // Check if file size is greater than 10MB (10 * 1024 * 1024 bytes)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size must be less than 10MB");
        return;
      }

      setIsImageUploading(true);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        const base64data = reader.result as string;
        try {
          const supabaseJWT = await getUnexpiredToken(session);

          const response = await fetch(
            `${import.meta.env.VITE_BACKEND_BASE_URL}/imageUpload`,
            {
              method: "POST",
              body: JSON.stringify({
                file: base64data.split(",")[1],
                fileName: file.name
                  .replace(/[^a-zA-Z0-9 ]/g, "")
                  .replace(/ /g, "_"),
              }),
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          );
          const data = (await response.json()) as {
            message?: string;
            url?: string;
            error?: string;
          };

          if (data.url) {
            setValue(`url('${data.url}')`);
            if (buildArbitraryClass) {
              onComboboxSelected(buildArbitraryClass(data.url));
            }
            setSet(true);
          }
        } catch (error) {
          console.error(error);
          toast.error("Error uploading image");
        } finally {
          setIsImageUploading(false);
        }
      };
    }
  };
  const handleBtnClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className="col-span-14">
      <div
        className={cn(
          "w-full h-32 relative rounded-lg mb-4 ",
          isImageUploading ? "cursor-not-allowed" : "cursor-pointer",
          "bg-black bg-opacity-80 bg-center bg-cover bg-no-repeat"
        )}
        style={{
          backgroundImage:
            value && value.startsWith("url('https")
              ? value
              : "url('https://storage.googleapis.com/tempo-public-assets/checker.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className={cn(
            "w-full h-full  rounded-lg",
            value && value.startsWith("url('https")
              ? ""
              : "bg-white  bg-opacity-80"
          )}
        >
          <div
            className={cn(
              "w-full h-full  rounded-lg",
              value && value.startsWith("url('https")
                ? ""
                : "bg-black  bg-opacity-80"
            )}
          >
            <div className="w-full h-full flex items-center justify-center">
              {isImageUploading ? (
                <Spinner />
              ) : (
                <>
                  <input
                    type="file"
                    ref={inputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageSelect}
                  />
                  <Button
                    type="button"
                    className="!bg-primary-0 !text-white !rounded-md !h-7"
                    onClick={handleBtnClick}
                  >
                    <Image className="h-4 w-4 mr-1" /> Upload Image
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CustomImageUpload);
